// src/containers/BookingRequest/components/UserProfileCard.tsx
import React from "react";
import {
  CheckCircleIcon,
  XMarkIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  ChatBubbleLeftRightIcon,
  UserCircleIcon,
  AcademicCapIcon,
  BriefcaseIcon,
} from "@heroicons/react/24/outline";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";

interface User {
  image?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  email_verified?: boolean;
  nationality?: string;
  live_in?: string;
  speak_language?: string;
  phone?: string;
  about_me?: string;
  created_at?: string;
  work?: string;
  school?: string;
  abroad_university?: string;
  home_university?: string;
  is_student?: boolean;
  gender?: string;
  tenant_type?: string;
  company_name?: string;
  work_place?: string;
  education_major?: string;
}

interface UserProfileCardProps {
  user: User;
  isHost: boolean;
  bookingStatus: string;
  userLabel: string;
}

const GENDER_LABELS: Record<string, string> = {
  male: "Male",
  female: "Female",
  non_binary: "Non-binary",
  prefer_not_to_say: "Prefer not to say",
};

const TENANT_TYPE_LABELS: Record<string, string> = {
  student: "Student",
  professional: "Professional",
  digital_nomad: "Digital Nomad",
  other: "Other",
};

const GenderIcon = () => (
  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);

const UserProfileCard: React.FC<UserProfileCardProps> = ({
  user,
  isHost,
  bookingStatus,
  userLabel,
}) => {
  const isConfirmed = bookingStatus === "confirmed";
  const displayName = user?.first_name
    ? isConfirmed
      ? `${user.first_name}${user.last_name ? ` ${user.last_name}` : ''}`
      : user.first_name
    : userLabel;
  const nationality = isHost ? user?.live_in : user?.nationality;

  const isGuest = userLabel === "Guest";
  const showUniversity = isGuest && (user?.abroad_university || user?.home_university);
  
  const tenantType = user?.tenant_type?.toLowerCase();
  const isStudent = tenantType === "student" || user?.is_student;
  const isProfessional = tenantType === "professional";

  const genderLabel = user?.gender ? (GENDER_LABELS[user.gender.toLowerCase()] ?? user.gender) : null;
  const tenantLabel = user?.tenant_type ? (TENANT_TYPE_LABELS[user.tenant_type.toLowerCase()] ?? user.tenant_type) : null;

  return (
    <div className="relative bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 shadow-2xl rounded-2xl overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -mr-20 -mt-20"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full -ml-16 -mb-16"></div>
      </div>

      <div className="relative p-4 sm:p-6">
        {/* Header */}
        <div className="flex gap-3 sm:gap-4 items-start mb-6">
          <div className="relative flex-shrink-0">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white rounded-full overflow-hidden shadow-lg ring-4 ring-white/30">
              {user?.image ? (
                <img src={user.image} alt={displayName} className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-orange-600 to-orange-700 text-white text-2xl sm:text-4xl font-bold">
                  {displayName?.charAt(0)?.toUpperCase() || "U"}
                </div>
              )}
            </div>
            {user?.email_verified && (
              <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1 shadow-lg ring-2 ring-white">
                <CheckBadgeIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg sm:text-xl text-white capitalize truncate mb-2">
              {displayName}
            </h3>

            <div className="flex flex-wrap gap-2 text-xs sm:text-sm text-white/90">
              {nationality && (
                <div className="flex items-center gap-1">
                  <GlobeAltIcon className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="truncate max-w-[120px]">{nationality}</span>
                </div>
              )}
              {genderLabel && (
                <div className="flex items-center gap-1">
                  <GenderIcon />
                  <span>{genderLabel}</span>
                </div>
              )}
              {tenantLabel && (
                <span className="px-2 py-0.5 bg-white/20 rounded-full text-[11px] font-semibold capitalize border border-white/30">
                  {tenantLabel}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {/* Email */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/20">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <EnvelopeIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                <h5 className="text-white text-xs sm:text-sm font-bold">Email</h5>
              </div>
              {user?.email_verified ? (
                <div className="flex items-center gap-1 px-2 py-1 bg-green-500/20 rounded-full">
                  <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 text-green-300" />
                  <span className="text-green-300 font-medium text-xs">Verified</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 px-2 py-1 bg-red-500/20 rounded-full">
                  <XMarkIcon className="h-3 w-3 sm:h-4 sm:w-4 text-red-300" />
                  <span className="text-red-300 font-medium text-xs">Not Verified</span>
                </div>
              )}
            </div>
            {isConfirmed ? (
              <p className="text-white/90 text-xs sm:text-sm break-all">{user?.email}</p>
            ) : (
              <p className="text-white/60 text-xs sm:text-sm italic">Hidden until booking confirmed</p>
            )}
          </div>

          {/* Phone */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/20">
            <div className="flex items-center gap-2 mb-2">
              <PhoneIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              <h5 className="text-white text-xs sm:text-sm font-bold">Phone</h5>
            </div>
            {isConfirmed ? (
              user?.phone ? (
                <p className="text-white/90 text-xs sm:text-sm">{user.phone}</p>
              ) : (
                <p className="text-white/60 text-xs sm:text-sm italic">Not provided</p>
              )
            ) : (
              <p className="text-white/60 text-xs sm:text-sm italic">Hidden until booking confirmed</p>
            )}
          </div>

          {/* University — guests/students */}
          {showUniversity && (
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/20">
              <div className="flex items-center gap-2 mb-3">
                <AcademicCapIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" />
                <h5 className="text-white text-xs sm:text-sm font-bold">University</h5>
              </div>
              {user?.abroad_university && (
                <div className="mb-3 last:mb-0">
                  <p className="text-white/70 text-xs font-semibold mb-1">Exchange University</p>
                  <p className="text-white/90 text-xs sm:text-sm">{user.abroad_university}</p>
                </div>
              )}
              {user?.home_university && (
                <div className={user?.abroad_university ? "pt-3 border-t border-white/20" : ""}>
                  <p className="text-white/70 text-xs font-semibold mb-1">Home University</p>
                  <p className="text-white/90 text-xs sm:text-sm">{user.home_university}</p>
                </div>
              )}
              {user?.education_major && (
                <div className="pt-3 border-t border-white/20">
                  <p className="text-white/70 text-xs font-semibold mb-1">Major</p>
                  <p className="text-white/90 text-xs sm:text-sm">{user.education_major}</p>
                </div>
              )}
            </div>
          )}

          {/* Work / Company — professionals or hosts */}
          {(isProfessional || isHost) && (user?.company_name || user?.work_place || user?.work) && (
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/20">
              <div className="flex items-center gap-2 mb-3">
                <BriefcaseIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" />
                <h5 className="text-white text-xs sm:text-sm font-bold">Work</h5>
              </div>
              {user?.company_name && (
                <div className="mb-2 last:mb-0">
                  <p className="text-white/70 text-xs font-semibold mb-1">Company</p>
                  <p className="text-white/90 text-xs sm:text-sm">{user.company_name}</p>
                </div>
              )}
              {(user?.work_place || user?.work) && (
                <div className={user?.company_name ? "pt-2 border-t border-white/20" : ""}>
                  <p className="text-white/70 text-xs font-semibold mb-1">Workplace</p>
                  <p className="text-white/90 text-xs sm:text-sm">{user.work_place || user.work}</p>
                </div>
              )}
            </div>
          )}

          {/* Languages */}
          {user?.speak_language && (
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/20">
              <div className="flex items-center gap-2 mb-2">
                <ChatBubbleLeftRightIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" />
                <h5 className="text-white text-xs sm:text-sm font-bold">Languages</h5>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {user.speak_language.split(",").map((lang, idx) => (
                  <span key={idx} className="px-2 py-1 bg-white/20 rounded-full text-xs text-white/90 capitalize">
                    {lang.trim()}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* About */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/20">
            <div className="flex items-center gap-2 mb-2">
              <UserCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              <h5 className="text-white text-xs sm:text-sm font-bold">About</h5>
            </div>
            <div className="max-h-24 sm:max-h-28 overflow-y-auto scrollbar-thin scrollbar-thumb-white/30 scrollbar-track-transparent hover:scrollbar-thumb-white/50 rounded">
              {user?.about_me ? (
                <p className="text-white/90 text-xs sm:text-sm break-words leading-relaxed">{user.about_me}</p>
              ) : (
                <p className="text-white/60 text-xs sm:text-sm italic">No information provided</p>
              )}
            </div>
          </div>
        </div>

        {/* Privacy notice */}
        {!isConfirmed && (
          <div className="mt-4 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
            <div className="flex gap-2">
              <svg className="w-4 h-4 text-white flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className="text-white/80 text-xs leading-relaxed">
                Contact information will be revealed once the booking is confirmed for your privacy and security.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfileCard;